import Rolling from '@assets/gif/rolling.gif';
import { Icon as BlueIcon } from '@blueprintjs/core';
import { NewTableMenu } from '@components/UpdatesPageMenus/NewTableMenu/NewTableMenu';
import Update, { IUpdateRawQueryList } from '@models/Update.model';
import { UpdateStatusType } from '@models/UpdateStatus.model';
import {
  LoadingImg,
  LoadingText,
  LoadMore,
} from '@pages/Products/components/ListProducts/ListProducts.styles';
import { UpdatesContext } from '@pages/Updates/Updates';
import { updateSort, updateSortDirection } from '@store/redux/features/updates';
import { useAppDispatch, useAppSelector } from '@store/redux/store';
import { format } from 'date-fns';
import { useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { ColumnTitle } from './ColumnTitle';
import { LoadingLine } from './components/LoadingLine/LoadingLine';
// import { RelatedTagsListWithTagDialog } from './components/RelatedTagsList/RelatedTagsListWithTagDialog';
import { TruncatedText } from './components/TruncatedText/TruncatedText';
import { getUpdate } from './getUpdate';
import { TableContentLoading } from './Updates.loading';
import {
  AffectsContiner,
  AffectsNumber,
  Container,
  Content,
  Heading,
  Internal,
  Line,
  LineInner,
  LineItem,
  LineMenu,
  LineText,
} from './Updates.styles';
import { SortUpdatesNewTypes } from './UpdatesNew.query';

interface IUpdatesNewRaw {
  updateStatuses: IUpdateRawQueryList[];
  loadMore: () => void;
  hasMore?: boolean;
}

const UpdatesNewRaw = ({
  updateStatuses,
  loadMore,
  hasMore,
}: IUpdatesNewRaw) => {
  const { updates, updatesOptimistic } = useAppSelector();
  const dispatch = useAppDispatch();
  const { ref, inView } = useInView();
  const { updateStatusId } = useContext(UpdatesContext);

  useEffect(() => {
    if (inView && hasMore) loadMore();
  }, [inView, hasMore]);
  const clickColumn = (column: SortUpdatesNewTypes) => {
    if (updates.options.sort === column) {
      if (updates.options.direction === 'ASC') {
        dispatch(updateSortDirection('DESC'));
        return;
      }
      if (updates.options.direction === 'DESC') {
        dispatch(updateSortDirection(undefined));
        return;
      }
      dispatch(updateSortDirection('ASC'));
    } else {
      dispatch(updateSortDirection('ASC'));
    }
    dispatch(updateSort(column));
  };

  const directionResult = (
    column: SortUpdatesNewTypes,
  ): 'up' | 'down' | undefined => {
    if (updates.options.sort === column) {
      if (updates.options.direction === 'ASC') {
        return 'up';
      }
      if (updates.options.direction === 'DESC') {
        return 'down';
      }
    }
    return undefined;
  };

  return (
    <Container>
      <Heading>
        <ColumnTitle
          label="Subject"
          onClick={() => clickColumn('subject')}
          direction={directionResult('subject')}
        />
        <ColumnTitle
          label="Affects"
          onClick={() => clickColumn('affects')}
          direction={directionResult('affects')}
        />
        {/* <ColumnTitle label="Related Tags" className="disabled" /> */}
        <div />
        <ColumnTitle
          label="Sent Date"
          onClick={() => clickColumn('date')}
          direction={directionResult('date')}
        />
        <ColumnTitle
          label="From"
          onClick={() => clickColumn('pManufacturers')}
          direction={directionResult('pManufacturers')}
        />
        <div />
      </Heading>

      {updateStatuses ? (
        <>
          <Content>
            {updatesOptimistic.backupNewUpdates
              .map(item => item.update)
              .filter(item => item.usType === UpdateStatusType.NEW)
              .map(update => {
                if (
                  updatesOptimistic.deletedUpdates?.find(
                    item => item.updateId === update.id,
                  ) ||
                  updateStatuses.find(item => item.id === update.id)
                ) {
                  return null;
                }

                // Optimistic updates are also changed internally for "real" updates
                const updateStatus = Update.fromRawQueryListApiModel(update);

                return (
                  <Line
                    key={updateStatus.id}
                    onClick={() => {
                      if (!updates.loadingUpdateId.includes(updateStatus.id)) {
                        updateStatusId.set(updateStatus.id);
                      }
                    }}
                  >
                    {updates.loadingUpdateId.includes(updateStatus.id) && (
                      <LoadingLine />
                    )}
                    <LineInner>
                      <LineItem>
                        <TruncatedText>{updateStatus.subject}</TruncatedText>
                      </LineItem>
                      <LineItem>
                        <LineText limitOfLines={1}>
                          <AffectsContiner>
                            <BlueIcon icon="cube" color="#1371FE" size={12} />
                            <AffectsNumber>
                              {updateStatus.affects}
                            </AffectsNumber>
                          </AffectsContiner>
                        </LineText>
                      </LineItem>
                      {/* <LineItem>
                        <RelatedTagsListWithTagDialog
                          tags={updateStatus.tags}
                        />
                      </LineItem> */}
                      <div />
                      <LineItem>
                        <LineText limitOfLines={1}>
                          {updateStatus.date &&
                            format(updateStatus.date, 'MMM do yyyy')}
                        </LineText>
                      </LineItem>
                      <LineItem>
                        <LineText limitOfLines={1}>
                          {(() => {
                            if (updateStatus.usStudioId) {
                              return <Internal>INTERNAL</Internal>;
                            }

                            if (updateStatus.type === 'products') {
                              return (
                                <TruncatedText>
                                  {updateStatus.pManufacturer}
                                </TruncatedText>
                              );
                            }
                            return '';
                          })()}
                        </LineText>
                      </LineItem>
                    </LineInner>
                    <LineMenu onClick={e => e.stopPropagation()}>
                      <NewTableMenu updateStatus={updateStatus} />
                    </LineMenu>
                  </Line>
                );
              })}
            {updateStatuses.map(update => {
              if (
                updatesOptimistic.deletedUpdates?.find(
                  item => item.updateId === update.id,
                )
              ) {
                return null;
              }

              const updateStatus = getUpdate(
                update,
                updatesOptimistic.backupUpdatedUpdates,
              );

              return (
                <Line
                  key={updateStatus.id}
                  onClick={() => {
                    if (!updates.loadingUpdateId.includes(updateStatus.id)) {
                      updateStatusId.set(updateStatus.id);
                    }
                  }}
                >
                  {updates.loadingUpdateId.includes(updateStatus.id) && (
                    <LoadingLine />
                  )}
                  <LineInner>
                    <LineItem>
                      <TruncatedText>{updateStatus.subject}</TruncatedText>
                    </LineItem>
                    <LineItem>
                      <LineText limitOfLines={1}>
                        <AffectsContiner>
                          <BlueIcon icon="cube" color="#1371FE" size={12} />
                          <AffectsNumber>{updateStatus.affects}</AffectsNumber>
                        </AffectsContiner>
                      </LineText>
                    </LineItem>
                    {/* <LineItem>
                      <RelatedTagsListWithTagDialog tags={updateStatus.tags} />
                    </LineItem> */}
                    <div />
                    <LineItem>
                      <LineText limitOfLines={1}>
                        {updateStatus.date &&
                          format(updateStatus.date, 'MMM do yyyy')}
                      </LineText>
                    </LineItem>
                    <LineItem>
                      <LineText limitOfLines={1}>
                        {(() => {
                          if (updateStatus.usStudioId) {
                            return <Internal>INTERNAL</Internal>;
                          }

                          if (updateStatus.type === 'products') {
                            return (
                              <TruncatedText>
                                {updateStatus.pManufacturer}
                              </TruncatedText>
                            );
                          }
                          return '';
                        })()}
                      </LineText>
                    </LineItem>
                  </LineInner>
                  <LineMenu onClick={e => e.stopPropagation()}>
                    <NewTableMenu updateStatus={updateStatus} />
                  </LineMenu>
                </Line>
              );
            })}
          </Content>
          <LoadMore ref={ref}>
            {hasMore ? (
              <LoadMore>
                <LoadingText>Loading more</LoadingText>
                <LoadingImg src={Rolling} alt="loading" />
              </LoadMore>
            ) : (
              <div />
            )}
          </LoadMore>
        </>
      ) : (
        <TableContentLoading />
      )}
    </Container>
  );
};

export { UpdatesNewRaw };
